import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { ROLE_LABELS, primaryRole } from "@/lib/roles";
import MultiRoleSelect from "@/components/MultiRoleSelect";
import {
  ArrowLeft, Save, User, Phone as PhoneIcon, Briefcase, GraduationCap, Banknote,
  Heart, FileText, Camera, Upload, Trash2, Plus, Printer, Download as DownloadIcon,
  ShieldCheck, KeyRound, Copy as CopyIcon, RotateCw, UserX
} from "lucide-react";
import { toast } from "sonner";

const NGN = (v) => "₦" + (Number(v) || 0).toLocaleString();

const TITLES = ["Mr","Mrs","Miss","Ms","Mallam","Malama","Hajiya","Alhaji","Ustadh","Ustadha","Dr","Prof","Sheikh","Engr"];
const SECTIONS = ["Academic","Admin","Islamiyyah","Tahfīz","Support","Finance","Maintenance"];
const EMP_TYPES = ["Full-time","Part-time","Contract","NYSC","Volunteer"];
const MARITAL = ["Single","Married","Divorced","Widowed"];
const RELIGIONS = ["Islam","Christianity","Other"];
const RELATIONSHIPS = ["Husband","Wife","Father","Mother","Brother","Sister","Son","Daughter","Guardian","Friend","Other"];

const compressImage = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onerror = reject;
    r.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const max = 480;
        let w = img.width, h = img.height;
        if (w > h) { if (w > max) { h = h * (max / w); w = max; } }
        else { if (h > max) { w = w * (max / h); h = max; } }
        const c = document.createElement("canvas"); c.width = w; c.height = h;
        c.getContext("2d").drawImage(img, 0, 0, w, h);
        resolve(c.toDataURL("image/jpeg", 0.75));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    r.readAsDataURL(file);
  });

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = (e) => resolve(e.target.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });

const StaffProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [staff, setStaff] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const photoInput = useRef(null);
  const docInput = useRef(null);
  const canEdit = ["super_admin", "director"].includes(user?.role);

  const currentUserRoles =
    Array.isArray(user?.roles) && user.roles.length
      ? user.roles
      : (user?.role ? [user.role] : []);

  const canManagePrivileges =
    currentUserRoles.includes("super_admin") ||
    (user?.effective_capabilities || []).includes(
      "organisation.privilege.manage"
    );

  const canReport = (user?.effective_capabilities || []).includes("report.create") || ["super_admin", "director", "teacher", "receptionist"].includes(user?.role);

  // login management state
  const [login, setLogin] = useState(null); // {has_login, email, role, account_status, last_login_at, ...}
  const [loginEdit, setLoginEdit] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: "", role: "", account_status: "active" });
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ email: "", role: "teacher", roles: ["teacher"] });
  const [credentialsModal, setCredentialsModal] = useState(null); // { email, temporary_password }

  // individual access and privilege management
  const [privileges, setPrivileges] = useState([]);
  const [authority, setAuthority] = useState(null);
  const [privilegesLoading, setPrivilegesLoading] = useState(false);
  const [privilegeOpen, setPrivilegeOpen] = useState(false);
  const [privilegeSaving, setPrivilegeSaving] = useState(false);

  const [privilegeForm, setPrivilegeForm] = useState({
    capability: "",
    effect: "grant",
    scope: "",
    start_at: "",
    end_at: "",
    reason: "",
  });

  const loadLogin = async () => {
    try {
      const { data } = await api.get(`/staff/${id}/login`);
      setLogin(data);
      if (data.has_login) {
        setLoginForm({
          email: data.email || "",
          role: data.role || "teacher",
          roles: Array.isArray(data.roles) && data.roles.length > 0 ? data.roles : [data.role || "teacher"],
          account_status: data.account_status || "active",
        });
      }
    } catch (e) { /* silent for non-admin */ }
  };

  const load = async () => {
    try {
      const { data } = await api.get(`/staff/${id}`);
      setStaff(data);
      setForm({
        ...data,
        qualifications: Array.isArray(data.qualifications) ? data.qualifications : [],
        courses: Array.isArray(data.courses) ? data.courses : [],
        roles: Array.isArray(data.roles) && data.roles.length > 0 ? data.roles : (data.role ? [data.role] : []),
      });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const loadPrivileges = async () => {
    if (!canManagePrivileges) return;

    setPrivilegesLoading(true);

    try {
      const [policyResponse, authorityResponse] = await Promise.all([
        api.get(`/organisation/staff/${id}/privileges`),
        api.get(`/organisation/authority/preview/${id}`),
      ]);

      setPrivileges(
        Array.isArray(policyResponse.data?.privileges)
          ? policyResponse.data.privileges
          : []
      );

      setAuthority(authorityResponse.data || null);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPrivilegesLoading(false);
    }
  };

  useEffect(() => {
    load();

    if (canEdit) {
      loadLogin();
    }

    if (canManagePrivileges) {
      loadPrivileges();
    }

    /* eslint-disable-next-line */
  }, [id]);

  const createLogin = async () => {
    try {
      const payload = {
        email: createForm.email,
        roles: createForm.roles && createForm.roles.length ? createForm.roles : [createForm.role || "teacher"],
      };
      const { data } = await api.post(`/staff/${id}/login`, payload);
      setCredentialsModal({ email: data.email, temporary_password: data.temporary_password });
      setCreateOpen(false);
      toast.success("Login created");
      loadLogin();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveLoginEdit = async () => {
    try {
      const payload = {
        email: loginForm.email,
        roles: loginForm.roles && loginForm.roles.length ? loginForm.roles : [loginForm.role],
        account_status: loginForm.account_status,
      };
      await api.patch(`/staff/${id}/login`, payload);
      toast.success("Login details updated"); setLoginEdit(false); loadLogin();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const resetPassword = async () => {
    if (!window.confirm("Generate a new temporary password? The staff member will be forced to change it on next login.")) return;
    try {
      const { data } = await api.post(`/staff/${id}/login/reset-password`);
      setCredentialsModal({ email: login.email, temporary_password: data.temporary_password });
      toast.success("Password reset");
      loadLogin();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const revokeLogin = async () => {
    if (!window.confirm("Permanently revoke this staff's login? They will lose all access. The HR record will remain.")) return;
    try {
      await api.delete(`/staff/${id}/login`);
      toast.success("Login revoked"); setLogin({ has_login: false, staff_id: id });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard?.writeText(text); toast.success("Copied to clipboard");
  };

  const savePrivilege = async () => {
    if (!privilegeForm.capability) {
      toast.error("Select a capability");
      return;
    }

    if (privilegeForm.reason.trim().length < 3) {
      toast.error("Please provide a clear reason");
      return;
    }

    if (
      privilegeForm.start_at &&
      privilegeForm.end_at &&
      new Date(privilegeForm.end_at).getTime() <=
        new Date(privilegeForm.start_at).getTime()
    ) {
      toast.error("End date must be after the start date");
      return;
    }

    setPrivilegeSaving(true);

    try {
      await api.post(`/organisation/staff/${id}/privileges`, {
        capability: privilegeForm.capability,
        effect: privilegeForm.effect,
        scope: null,

        start_at: privilegeForm.start_at
          ? new Date(privilegeForm.start_at).toISOString()
          : null,

        end_at: privilegeForm.end_at
          ? new Date(privilegeForm.end_at).toISOString()
          : null,

        reason: privilegeForm.reason.trim(),

        request_key:
          globalThis.crypto?.randomUUID?.() ||
          `priv-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      });

      toast.success(
        privilegeForm.effect === "restrict"
          ? "Access restriction recorded"
          : "Access privilege granted"
      );

      setPrivilegeOpen(false);

      setPrivilegeForm({
        capability: "",
        effect: "grant",
        scope: "",
        start_at: "",
        end_at: "",
        reason: "",
      });

      await loadPrivileges();

    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPrivilegeSaving(false);
    }
  };

  const revokePrivilege = async (privilege) => {
    const reason = window.prompt(
      `Reason for revoking this ${
        privilege.effect === "restrict"
          ? "restriction"
          : "grant"
      }?`
    );

    if (!reason) return;

    if (reason.trim().length < 3) {
      toast.error("Please provide a clear revocation reason");
      return;
    }

    try {
      await api.post(
        `/organisation/staff/${id}/privileges/${privilege.id}/revoke`,
        {
          reason: reason.trim(),

          request_key:
            globalThis.crypto?.randomUUID?.() ||
            `revoke-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        }
      );

      toast.success("Access rule revoked; audit history preserved");

      await loadPrivileges();

    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (!form) return <div className="text-[#475569]" data-testid="staff-profile-loading">Loading profile...</div>;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      delete payload.id; delete payload.created_at; delete payload.documents; delete payload.passport_photo;
      if (payload.salary !== undefined && payload.salary !== "") payload.salary = Number(payload.salary);
      const { data } = await api.patch(`/staff/${id}`, payload);
      setStaff(data); toast.success("Profile saved");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const onPhotoPick = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    try {
      const dataUrl = await compressImage(f);
      const { data } = await api.patch(`/staff/${id}`, { passport_photo: dataUrl });
      setStaff(data); setForm((p) => ({ ...p, passport_photo: dataUrl }));
      toast.success("Passport photo updated");
    } catch (err) { toast.error("Failed to upload photo"); }
    e.target.value = "";
  };

  const onDocPick = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 3 * 1024 * 1024) { toast.error("File too large (max 3MB)"); e.target.value = ""; return; }
    try {
      const dataUrl = await fileToDataUrl(f);
      const { data } = await api.post(`/staff/${id}/documents`, {
        name: f.name, type: f.type, size: f.size, data: dataUrl,
      });
      setStaff(data); toast.success(`${f.name} uploaded`);
    } catch (err) { toast.error(formatApiError(err)); }
    e.target.value = "";
  };

  const deleteDoc = async (doc_id) => {
    if (!window.confirm("Delete this document?")) return;
    try {
      const { data } = await api.delete(`/staff/${id}/documents/${doc_id}`);
      setStaff(data); toast.success("Document removed");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const addQualification = () => set("qualifications", [...(form.qualifications || []), { level: "", institution: "", course: "", year: "" }]);
  const setQual = (i, k, v) => {
    const next = [...(form.qualifications || [])]; next[i] = { ...next[i], [k]: v }; set("qualifications", next);
  };
  const removeQual = (i) => set("qualifications", (form.qualifications || []).filter((_, idx) => idx !== i));

  const initials = (form.full_name || "").split(" ").slice(0,2).map(p=>p[0]).join("").toUpperCase();

  return (
    <div className="space-y-6" data-testid="staff-profile-page">
      {/* Top bar */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <Button variant="ghost" onClick={() => navigate("/app/staff")} className="text-[#1B2C5A]" data-testid="back-to-staff">
          <ArrowLeft className="w-4 h-4 mr-1.5" /> Staff Directory
        </Button>
        <div className="flex items-center gap-2">
          {canReport && <Button variant="outline" onClick={() => navigate(`/app/reports/new?person_type=staff&person_id=${id}&name=${encodeURIComponent(form.full_name || "Staff")}`)} data-testid="staff-create-report">Create Report</Button>}
          <Button variant="outline" onClick={() => navigate(`/app/id-cards?type=staff&id=${id}`)} data-testid="staff-id-card">ID Card</Button>
          <Button variant="outline" onClick={() => window.print()} className="border-[#E2E8F0]" data-testid="print-profile">
            <Printer className="w-4 h-4 mr-1.5" /> Print
          </Button>
          {canEdit && (
            <Button onClick={save} disabled={saving} className="bg-[#1B2C5A] hover:bg-[#14224A] text-white" data-testid="save-profile">
              <Save className="w-4 h-4 mr-1.5" /> {saving ? "Saving..." : "Save Changes"}
            </Button>
          )}
        </div>
      </div>

      {/* Header card */}
      <Card className="p-6 border-[#E2E8F0]">
        <div className="flex flex-col md:flex-row md:items-center gap-6">
          <div className="relative">
            <div className="w-32 h-32 rounded-2xl bg-[#1B2C5A]/5 overflow-hidden grid place-items-center text-3xl font-display text-[#1B2C5A] ring-2 ring-[#9DCD3F]/30">
              {staff?.passport_photo
                ? <img src={staff.passport_photo} alt="Passport" className="w-full h-full object-cover" data-testid="passport-img" />
                : initials || "DFA"}
            </div>
            {canEdit && (
              <button
                onClick={() => photoInput.current?.click()}
                className="absolute -bottom-2 -right-2 w-10 h-10 rounded-full bg-[#9DCD3F] hover:bg-[#88BB35] text-[#1B2C5A] grid place-items-center shadow-md"
                data-testid="upload-photo-btn"
                title="Upload passport photo"
              >
                <Camera className="w-4 h-4" />
              </button>
            )}
            <input ref={photoInput} type="file" accept="image/*" className="hidden" onChange={onPhotoPick} data-testid="photo-input" />
          </div>
          <div className="flex-1">
            <div className="overline text-[#9DCD3F]">{form.employee_no || "Employee"}</div>
            <h1 className="font-display text-3xl text-[#1B2C5A] mt-1 leading-tight">{form.title ? `${form.title} ` : ""}{form.full_name || "Unnamed Staff"}</h1>
            <div className="text-[#475569] mt-1">{form.designation || ROLE_LABELS[form.role] || "—"} · {form.department || form.job_section || "—"}</div>
            <div className="mt-3 flex flex-wrap gap-2">
              {(form.roles && form.roles.length ? form.roles : [form.role]).filter(Boolean).map((r) => (
                <Badge key={r}>{ROLE_LABELS[r] || r}</Badge>
              ))}
              {form.employment_type && <Badge>{form.employment_type}</Badge>}
              {form.job_section && <Badge>{form.job_section}</Badge>}
              <Badge variant="success">{form.status || "active"}</Badge>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 md:gap-6 md:max-w-sm w-full md:w-auto">
            <Stat label="Date of Joining" value={form.date_of_joining || "—"} />
            <Stat label="Salary" value={form.salary ? NGN(form.salary) : "—"} />
            <Stat label="Phone" value={form.phone || "—"} />
            <Stat label="Email" value={form.email || "—"} />
          </div>
        </div>
      </Card>

      {/* Tabs */}
      <Tabs defaultValue="personal" className="w-full">
        <TabsList className="bg-white border border-[#E2E8F0] h-auto flex-wrap" data-testid="profile-tabs">
          <TabsTrigger value="personal"   data-testid="tab-personal"><User className="w-3.5 h-3.5 mr-1.5" /> Personal</TabsTrigger>
          <TabsTrigger value="contact"    data-testid="tab-contact"><PhoneIcon className="w-3.5 h-3.5 mr-1.5" /> Contact</TabsTrigger>
          <TabsTrigger value="employment" data-testid="tab-employment"><Briefcase className="w-3.5 h-3.5 mr-1.5" /> Employment</TabsTrigger>
          <TabsTrigger value="education"  data-testid="tab-education"><GraduationCap className="w-3.5 h-3.5 mr-1.5" /> Education</TabsTrigger>
          <TabsTrigger value="bank"       data-testid="tab-bank"><Banknote className="w-3.5 h-3.5 mr-1.5" /> Bank</TabsTrigger>
          <TabsTrigger value="nok"        data-testid="tab-nok"><Heart className="w-3.5 h-3.5 mr-1.5" /> Next of Kin</TabsTrigger>
          <TabsTrigger value="documents"  data-testid="tab-documents"><FileText className="w-3.5 h-3.5 mr-1.5" /> Documents</TabsTrigger>
          {canEdit && <TabsTrigger value="login" data-testid="tab-login"><KeyRound className="w-3.5 h-3.5 mr-1.5" /> Login & Security</TabsTrigger>}

          {canManagePrivileges && (
            <TabsTrigger
              value="privileges"
              data-testid="tab-privileges"
            >
              <ShieldCheck className="w-3.5 h-3.5 mr-1.5" />
              Access & Privileges
            </TabsTrigger>
          )}
        </TabsList>

        {/* Personal */}
        <TabsContent value="personal" className="mt-4">
          <Card className="p-6 border-[#E2E8F0]">
            <SectionTitle>Personal Information</SectionTitle>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
              <Field label="Title">
                <Select value={form.title || ""} onValueChange={(v) => set("title", v)}>
                  <SelectTrigger data-testid="f-title"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>{TITLES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Full Name" required full><Input value={form.full_name || ""} onChange={(e) => set("full_name", e.target.value)} data-testid="f-full-name" /></Field>
              <Field label="Date of Birth"><Input type="date" value={form.date_of_birth || ""} onChange={(e) => set("date_of_birth", e.target.value)} data-testid="f-dob" /></Field>
              <Field label="Gender">
                <Select value={form.gender || ""} onValueChange={(v) => set("gender", v)}>
                  <SelectTrigger data-testid="f-gender"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent><SelectItem value="male">Male</SelectItem><SelectItem value="female">Female</SelectItem></SelectContent>
                </Select>
              </Field>
              <Field label="Marital Status">
                <Select value={form.marital_status || ""} onValueChange={(v) => set("marital_status", v)}>
                  <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>{MARITAL.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Religion">
                <Select value={form.religion || ""} onValueChange={(v) => set("religion", v)}>
                  <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>{RELIGIONS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Nationality"><Input value={form.nationality || ""} onChange={(e) => set("nationality", e.target.value)} placeholder="Nigerian" /></Field>
              <Field label="State of Origin"><Input value={form.state_of_origin || ""} onChange={(e) => set("state_of_origin", e.target.value)} /></Field>
              <Field label="LGA"><Input value={form.lga || ""} onChange={(e) => set("lga", e.target.value)} /></Field>
            </div>
          </Card>
        </TabsContent>

        {/* Contact */}
        <TabsContent value="contact" className="mt-4">
          <Card className="p-6 border-[#E2E8F0]">
            <SectionTitle>Contact Details</SectionTitle>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <Field label="Phone (Primary)"><Input value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} data-testid="f-phone" /></Field>
              <Field label="Phone (Alternate)"><Input value={form.phone_alt || ""} onChange={(e) => set("phone_alt", e.target.value)} /></Field>
              <Field label="Email"><Input type="email" value={form.email || ""} onChange={(e) => set("email", e.target.value)} data-testid="f-email" /></Field>
              <Field label="Residential Address" full><Textarea rows={3} value={form.address || ""} onChange={(e) => set("address", e.target.value)} /></Field>
            </div>
          </Card>
        </TabsContent>

        {/* Employment */}
        <TabsContent value="employment" className="mt-4">
          <Card className="p-6 border-[#E2E8F0]">
            <SectionTitle>Employment Details</SectionTitle>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
              <Field label="Employee No."><Input value={form.employee_no || ""} onChange={(e) => set("employee_no", e.target.value)} /></Field>
              <Field label="Roles (multi-select)" full>
                <MultiRoleSelect
                  value={form.roles && form.roles.length ? form.roles : (form.role ? [form.role] : [])}
                  onChange={(roles) => setForm((f) => ({ ...f, roles, role: primaryRole(roles) }))}
                  testId="employment-roles"
                />
              </Field>
              <Field label="Designation"><Input value={form.designation || ""} onChange={(e) => set("designation", e.target.value)} data-testid="f-designation" /></Field>
              <Field label="Department"><Input value={form.department || ""} onChange={(e) => set("department", e.target.value)} /></Field>
              <Field label="Job Section">
                <Select value={form.job_section || ""} onValueChange={(v) => set("job_section", v)}>
                  <SelectTrigger data-testid="f-section"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>{SECTIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Employment Type">
                <Select value={form.employment_type || ""} onValueChange={(v) => set("employment_type", v)}>
                  <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>{EMP_TYPES.map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Date of First Appointment"><Input type="date" value={form.date_first_appointment || ""} onChange={(e) => set("date_first_appointment", e.target.value)} data-testid="f-first-app" /></Field>
              <Field label="Date of Assumption of Duty"><Input type="date" value={form.date_assumption_of_duty || ""} onChange={(e) => set("date_assumption_of_duty", e.target.value)} data-testid="f-assumption" /></Field>
              <Field label="Date of Joining"><Input type="date" value={form.date_of_joining || ""} onChange={(e) => set("date_of_joining", e.target.value)} /></Field>
              <Field label="Salary (₦)"><Input type="number" value={form.salary || ""} onChange={(e) => set("salary", e.target.value)} /></Field>
              <Field label="Status">
                <Select value={form.status || "active"} onValueChange={(v) => set("status", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="on_leave">On Leave</SelectItem>
                    <SelectItem value="suspended">Suspended</SelectItem>
                    <SelectItem value="terminated">Terminated</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>
          </Card>
        </TabsContent>

        {/* Education */}
        <TabsContent value="education" className="mt-4">
          <Card className="p-6 border-[#E2E8F0]">
            <div className="flex items-center justify-between">
              <SectionTitle>Educational Qualifications & Courses</SectionTitle>
              {canEdit && <Button size="sm" variant="outline" onClick={addQualification} data-testid="add-qual"><Plus className="w-3.5 h-3.5 mr-1" /> Add</Button>}
            </div>
            <div className="space-y-3 mt-4">
              {(form.qualifications || []).length === 0 && <div className="text-sm text-[#475569]">No qualifications added.</div>}
              {(form.qualifications || []).map((q, i) => (
                <div key={i} className="grid grid-cols-1 md:grid-cols-12 gap-3 p-3 rounded-lg border border-[#E2E8F0]" data-testid={`qual-${i}`}>
                  <div className="md:col-span-2">
                    <Label className="text-xs text-[#475569]">Level</Label>
                    <Input value={q.level || ""} onChange={(e) => setQual(i, "level", e.target.value)} placeholder="B.Sc / NCE / M.Sc" />
                  </div>
                  <div className="md:col-span-4">
                    <Label className="text-xs text-[#475569]">Institution</Label>
                    <Input value={q.institution || ""} onChange={(e) => setQual(i, "institution", e.target.value)} />
                  </div>
                  <div className="md:col-span-4">
                    <Label className="text-xs text-[#475569]">Course / Discipline</Label>
                    <Input value={q.course || ""} onChange={(e) => setQual(i, "course", e.target.value)} />
                  </div>
                  <div className="md:col-span-1">
                    <Label className="text-xs text-[#475569]">Year</Label>
                    <Input value={q.year || ""} onChange={(e) => setQual(i, "year", e.target.value)} />
                  </div>
                  <div className="md:col-span-1 flex items-end justify-end">
                    <Button size="sm" variant="ghost" onClick={() => removeQual(i)} className="text-red-700"><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-6">
              <Label className="text-[#1B2C5A] text-sm">Courses (comma-separated)</Label>
              <Textarea
                rows={2}
                value={(form.courses || []).join(", ")}
                onChange={(e) => set("courses", e.target.value.split(",").map(s => s.trim()).filter(Boolean))}
                placeholder="Mathematics, Further Mathematics, Quantitative Reasoning"
                data-testid="f-courses"
              />
            </div>
          </Card>
        </TabsContent>

        {/* Bank */}
        <TabsContent value="bank" className="mt-4">
          <Card className="p-6 border-[#E2E8F0]">
            <SectionTitle>Bank & Payroll</SectionTitle>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <Field label="Bank Name"><Input value={form.bank_name || ""} onChange={(e) => set("bank_name", e.target.value)} data-testid="f-bank" /></Field>
              <Field label="Account Number"><Input value={form.bank_account_number || ""} onChange={(e) => set("bank_account_number", e.target.value)} /></Field>
              <Field label="Account Name"><Input value={form.bank_account_name || ""} onChange={(e) => set("bank_account_name", e.target.value)} /></Field>
              <Field label="BVN"><Input value={form.bvn || ""} onChange={(e) => set("bvn", e.target.value)} /></Field>
            </div>
          </Card>
        </TabsContent>

        {/* Next of Kin */}
        <TabsContent value="nok" className="mt-4">
          <Card className="p-6 border-[#E2E8F0]">
            <SectionTitle>Next of Kin</SectionTitle>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <Field label="Full Name"><Input value={form.nok_name || ""} onChange={(e) => set("nok_name", e.target.value)} data-testid="f-nok-name" /></Field>
              <Field label="Relationship">
                <Select value={form.nok_relationship || ""} onValueChange={(v) => set("nok_relationship", v)}>
                  <SelectTrigger data-testid="f-nok-rel"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>{RELATIONSHIPS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Phone"><Input value={form.nok_phone || ""} onChange={(e) => set("nok_phone", e.target.value)} /></Field>
              <Field label="Address" full><Textarea rows={3} value={form.nok_address || ""} onChange={(e) => set("nok_address", e.target.value)} /></Field>
            </div>
          </Card>
        </TabsContent>

        {/* Documents */}
        <TabsContent value="documents" className="mt-4">
          <Card className="p-6 border-[#E2E8F0]">
            <div className="flex items-center justify-between">
              <SectionTitle>Documents</SectionTitle>
              {canEdit && (
                <>
                  <Button size="sm" onClick={() => docInput.current?.click()} className="bg-[#1B2C5A] hover:bg-[#14224A] text-white" data-testid="upload-doc-btn">
                    <Upload className="w-4 h-4 mr-1.5" /> Upload Document
                  </Button>
                  <input ref={docInput} type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" className="hidden" onChange={onDocPick} data-testid="doc-input" />
                </>
              )}
            </div>
            <p className="text-xs text-[#475569] mt-1">PDF, images, or Word documents — up to 3MB each. Certificates, contracts, NIN, ID copies etc.</p>
            <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-3">
              {(staff?.documents || []).length === 0 && (
                <div className="md:col-span-2 text-center text-sm text-[#475569] py-8 border border-dashed border-[#E2E8F0] rounded-lg">No documents uploaded yet.</div>
              )}
              {(staff?.documents || []).map((d) => (
                <div key={d.id} className="flex items-center gap-3 p-3 border border-[#E2E8F0] rounded-lg bg-white" data-testid={`doc-${d.id}`}>
                  <div className="w-10 h-10 rounded-lg bg-[#9DCD3F]/15 text-[#1B2C5A] grid place-items-center"><FileText className="w-5 h-5" /></div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-[#1B2C5A] truncate">{d.name}</div>
                    <div className="text-xs text-[#475569]">{Math.round((d.size || 0) / 1024)} KB · {new Date(d.uploaded_at).toLocaleDateString()}</div>
                  </div>
                  <a href={d.data} download={d.name} className="text-[#1B2C5A] hover:text-[#9DCD3F] p-2" title="Download" data-testid={`doc-download-${d.id}`}>
                    <DownloadIcon className="w-4 h-4" />
                  </a>
                  {canEdit && (
                    <button onClick={() => deleteDoc(d.id)} className="text-red-700 hover:text-red-900 p-2" data-testid={`doc-delete-${d.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>

        {/* Login & Security */}
        {canEdit && (
          <TabsContent value="login" className="mt-4">
            <LoginAccessSection
              login={login}
              loginEdit={loginEdit}
              setLoginEdit={setLoginEdit}
              loginForm={loginForm}
              setLoginForm={setLoginForm}
              saveLoginEdit={saveLoginEdit}
              resetPassword={resetPassword}
              revokeLogin={revokeLogin}
              openCreate={() => {
                setCreateForm({
                  email: form.email || "",
                  role: form.role || "teacher",
                });
                setCreateOpen(true);
              }}
              copyToClipboard={copyToClipboard}
            />
          </TabsContent>
        )}

        {canManagePrivileges && (
          <TabsContent value="privileges" className="mt-4">
            <StaffPrivilegesSection
              privileges={privileges}
              authority={authority}
              loading={privilegesLoading}
              onAdd={() => setPrivilegeOpen(true)}
              onRevoke={revokePrivilege}
            />
          </TabsContent>
        )}

      </Tabs>

      {/* Individual Access Rule */}
      <Dialog open={privilegeOpen} onOpenChange={setPrivilegeOpen}>
        <DialogContent className="max-w-xl">

          <DialogHeader>
            <DialogTitle className="font-display text-[#1B2C5A]">
              Add Individual Access Rule
            </DialogTitle>

            <DialogDescription>
              Give this staff member specific access without promoting them
              to a broader role.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">

            <Field label="Decision">
              <Select
                value={privilegeForm.effect}
                onValueChange={(value) =>
                  setPrivilegeForm((p) => ({
                    ...p,
                    effect: value,
                  }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>

                <SelectContent>
                  <SelectItem value="grant">
                    Grant Access
                  </SelectItem>

                  <SelectItem value="restrict">
                    Restrict Access
                  </SelectItem>
                </SelectContent>
              </Select>
            </Field>

            <Field label="Capability">
              <Select
                value={privilegeForm.capability}
                onValueChange={(value) =>
                  setPrivilegeForm((p) => ({
                    ...p,
                    capability: value,
                  }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select access" />
                </SelectTrigger>

                <SelectContent>
                  {PRIVILEGE_OPTIONS.map((item) => (
                    <SelectItem
                      key={item.value}
                      value={item.value}
                    >
                      {item.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {privilegeForm.capability && (
                <p className="text-xs font-mono text-[#64748B] mt-1">
                  {privilegeForm.capability}
                </p>
              )}
            </Field>

            <Field label="Scope">
              <Select
                value={privilegeForm.scope || "__default__"}
                onValueChange={(value) =>
                  setPrivilegeForm((p) => ({
                    ...p,
                    scope:
                      value === "__default__"
                        ? ""
                        : value,
                  }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>

                <SelectContent>
                  <SelectItem value="__default__">
                    Normal scope
                  </SelectItem>

                  {PRIVILEGE_SCOPES.map((scope) => (
                    <SelectItem
                      key={scope}
                      value={scope}
                    >
                      {scopeLabel(scope)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

              <Field label="Starts">
                <Input
                  type="datetime-local"
                  value={privilegeForm.start_at}
                  onChange={(e) =>
                    setPrivilegeForm((p) => ({
                      ...p,
                      start_at: e.target.value,
                    }))
                  }
                />
              </Field>

              <Field label="Ends">
                <Input
                  type="datetime-local"
                  value={privilegeForm.end_at}
                  onChange={(e) =>
                    setPrivilegeForm((p) => ({
                      ...p,
                      end_at: e.target.value,
                    }))
                  }
                />
              </Field>

            </div>

            <Field label="Reason" required>
              <Textarea
                rows={3}
                value={privilegeForm.reason}
                onChange={(e) =>
                  setPrivilegeForm((p) => ({
                    ...p,
                    reason: e.target.value,
                  }))
                }
                placeholder="Example: Temporarily responsible for Basic Unit attendance while the Unit Head is away."
              />
            </Field>

            {privilegeForm.effect === "restrict" && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm">
                An active restriction overrides access inherited from
                roles, positions, assignments, delegations and individual
                grants.
              </div>
            )}

          </div>

          <DialogFooter>

            <Button
              variant="outline"
              onClick={() => setPrivilegeOpen(false)}
            >
              Cancel
            </Button>

            <Button
              onClick={savePrivilege}
              disabled={privilegeSaving}
              className="bg-[#1B2C5A] hover:bg-[#14224A]"
            >
              {privilegeSaving
                ? "Saving..."
                : privilegeForm.effect === "restrict"
                  ? "Apply Restriction"
                  : "Grant Access"}
            </Button>

          </DialogFooter>

        </DialogContent>
      </Dialog>

      {/* Create login dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display text-[#1B2C5A]">Create Staff Login</DialogTitle>
            <DialogDescription className="text-[#475569]">
              A temporary password will be auto-generated. Share it with the staff member — they will be forced to change it on first login.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 mt-2">
            <Field label="Login Email">
              <Input value={createForm.email} onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })} placeholder="auto-generated if blank" data-testid="create-login-email" />
            </Field>
            <Field label="Roles (multi-select)">
              <MultiRoleSelect
                value={createForm.roles}
                onChange={(roles) => setCreateForm({ ...createForm, roles, role: primaryRole(roles) })}
                testId="create-login-roles"
              />
            </Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={createLogin} className="bg-[#1B2C5A] hover:bg-[#14224A] text-white" data-testid="create-login-submit">
              <KeyRound className="w-4 h-4 mr-1.5" /> Generate Login
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Credentials shown-once modal */}
      <Dialog open={!!credentialsModal} onOpenChange={(o) => !o && setCredentialsModal(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display text-[#1B2C5A]">Temporary Credentials</DialogTitle>
            <DialogDescription className="text-[#475569]">
              Copy these credentials now — for security, the password will <strong>not be shown again</strong>. The staff member must change it on first login.
            </DialogDescription>
          </DialogHeader>
          {credentialsModal && (
            <div className="space-y-3 mt-2">
              <div className="p-3 rounded-lg bg-[#9DCD3F]/10 border border-[#9DCD3F]/40 flex items-center justify-between">
                <div>
                  <div className="overline text-[#9DCD3F]">Email</div>
                  <div className="font-mono text-[#1B2C5A]" data-testid="creds-email">{credentialsModal.email}</div>
                </div>
                <Button size="sm" variant="outline" onClick={() => copyToClipboard(credentialsModal.email)}><CopyIcon className="w-3.5 h-3.5" /></Button>
              </div>
              <div className="p-3 rounded-lg bg-[#1B2C5A]/5 border border-[#1B2C5A]/15 flex items-center justify-between">
                <div>
                  <div className="overline text-[#9DCD3F]">Temporary Password</div>
                  <div className="font-mono text-lg text-[#1B2C5A]" data-testid="creds-password">{credentialsModal.temporary_password}</div>
                </div>
                <Button size="sm" variant="outline" onClick={() => copyToClipboard(credentialsModal.temporary_password)}><CopyIcon className="w-3.5 h-3.5" /></Button>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setCredentialsModal(null)} className="bg-[#1B2C5A] hover:bg-[#14224A] text-white" data-testid="creds-close">I've saved them</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const PRIVILEGE_OPTIONS = [
  { value: "student.view", label: "Students — View" },
  { value: "student.create", label: "Students — Create" },
  { value: "student.edit", label: "Students — Edit" },
  { value: "student.manage_enrollment", label: "Students — Manage Enrolment" },
  { value: "student.archive", label: "Students — Archive" },

  { value: "guardian.view", label: "Guardians — View" },
  { value: "guardian.manage", label: "Guardians — Manage" },
  { value: "guardian.link", label: "Guardians — Link to Students" },

  { value: "attendance.view", label: "Student Attendance — View" },
  { value: "attendance.mark", label: "Student Attendance — Mark" },

  { value: "attendance.staff.clock", label: "Staff Attendance — Clock In/Out" },
  { value: "attendance.staff.view_own", label: "Staff Attendance — View Own" },
  { value: "attendance.staff.view_unit", label: "Staff Attendance — View Unit" },
  { value: "attendance.staff.view_institution", label: "Staff Attendance — View Institution" },
  { value: "attendance.staff.manage_schedule", label: "Staff Attendance — Manage Schedules" },
  { value: "attendance.staff.manage_geofence", label: "Staff Attendance — Manage Geofence" },
  { value: "attendance.staff.manage_devices", label: "Staff Attendance — Manage Devices" },
  { value: "attendance.staff.manage_general_devices", label: "Staff Attendance — Manage General Devices" },
  { value: "attendance.staff.review_exception", label: "Staff Attendance — Review Exceptions" },
  { value: "attendance.staff.adjust", label: "Staff Attendance — Adjust Records" },
  { value: "attendance.staff.analytics", label: "Staff Attendance — Analytics" },
  { value: "attendance.staff.view_precise_location", label: "Staff Attendance — View Precise Location" },
  { value: "attendance.staff.export", label: "Staff Attendance — Export" },

  { value: "assessment.view", label: "Assessment — View" },
  { value: "assessment.enter_marks", label: "Assessment — Enter Marks" },
  { value: "assessment.review", label: "Assessment — Review" },
  { value: "assessment.configure", label: "Assessment — Configure" },
  { value: "assessment.publish", label: "Assessment — Publish" },
  { value: "assessment.unlock", label: "Assessment — Unlock" },

  { value: "subject.manage", label: "Subjects — Manage" },
  { value: "promotion.execute", label: "Promotion — Execute" },

  { value: "staff.view_directory", label: "Staff — View Directory" },
  { value: "staff.view_sensitive", label: "Staff — View Sensitive Information" },
  { value: "staff.manage", label: "Staff — Manage Records" },

  { value: "finance.account.view", label: "Finance — View Accounts" },
  { value: "finance.payment.record", label: "Finance — Record Payments" },
  { value: "finance.payment.allocate", label: "Finance — Allocate Payments" },
  { value: "finance.reversal.request", label: "Finance — Request Reversal" },
  { value: "finance.reversal.finalize", label: "Finance — Finalise Reversal" },
  { value: "finance.refund.request", label: "Finance — Request Refund" },
  { value: "finance.refund.finalize", label: "Finance — Finalise Refund" },
  { value: "finance.fee_structure.manage", label: "Finance — Manage Fee Structure" },
  { value: "finance.scholarship.manage", label: "Finance — Manage Scholarships" },
  { value: "finance.reconcile", label: "Finance — Reconcile" },
  { value: "finance.audit.view", label: "Finance — View Audit" },
  { value: "finance.payment_status.view_class", label: "Finance — View Class Payment Status" },
  { value: "finance.payment_status.view_student", label: "Finance — View Student Payment Status" },

  { value: "asset.view", label: "Assets — View" },
  { value: "asset.manage", label: "Assets — Manage" },
  { value: "visitor.manage", label: "Visitors — Manage" },
  { value: "notification.broadcast", label: "Notifications — Broadcast" },
  { value: "dashboard.view", label: "Dashboard — View" },

  { value: "report.create", label: "Reports — Create" },
  { value: "report.view_own", label: "Reports — View Own" },
  { value: "report.view_assigned", label: "Reports — View Assigned" },
  { value: "report.view_unit", label: "Reports — View Unit" },
  { value: "report.view_institution", label: "Reports — View Institution" },
  { value: "report.action", label: "Reports — Take Action" },
  { value: "report.escalate", label: "Reports — Escalate" },
  { value: "report.reassign", label: "Reports — Reassign" },
  { value: "report.resolve", label: "Reports — Resolve" },
  { value: "report.close", label: "Reports — Close" },
  { value: "report.reopen", label: "Reports — Reopen" },
  { value: "report.manage_sensitive", label: "Reports — Manage Sensitive Reports" },
  { value: "report.confidential_route", label: "Reports — Confidential Routing" },
  { value: "report.confidential_direct", label: "Reports — Confidential Direct" },
  { value: "report.parent_engagement.create", label: "Parent Engagement — Create" },
  { value: "report.parent_engagement.send", label: "Parent Engagement — Send" },
  { value: "report.parent_engagement.view", label: "Parent Engagement — View" },
  { value: "report.analytics.view", label: "Reports — Analytics" },

  { value: "organisation.view", label: "Organisation — View" },
  { value: "organisation.manage", label: "Organisation — Manage" },
  { value: "organisation.assignment.manage", label: "Organisation — Manage Assignments" },
  { value: "organisation.delegation.manage", label: "Organisation — Manage Delegations" },

  { value: "id_card.view", label: "ID Cards — View" },
  { value: "id_card.generate", label: "ID Cards — Generate" },
  { value: "id_card.print", label: "ID Cards — Print" },
  { value: "id_card.bulk_generate", label: "ID Cards — Bulk Generate" },
  { value: "id_card.issue", label: "ID Cards — Issue" },
  { value: "id_card.reissue", label: "ID Cards — Reissue" },
  { value: "id_card.revoke", label: "ID Cards — Revoke" },
];

const PRIVILEGE_SCOPES = [
  "self",
  "linked_children",
  "assigned_students",
  "assigned_class",
  "assigned_unit",
  "academic_all",
  "operations_all",
  "finance_all",
  "institution_all",
  "executive",
];

const capabilityLabel = (capability) => {
  const found = PRIVILEGE_OPTIONS.find(
    (item) => item.value === capability
  );

  return found?.label || capability;
};

const scopeLabel = (scope) => ({
  self: "Self only",
  linked_children: "Linked children",
  assigned_students: "Assigned students",
  assigned_class: "Assigned class",
  assigned_unit: "Assigned unit",
  academic_all: "All academics",
  operations_all: "All operations",
  finance_all: "All finance",
  institution_all: "Whole institution",
  executive: "Executive",
}[scope] || scope || "Normal scope");

const privilegeDate = (value) => {
  if (!value) return "No limit";

  const date = new Date(value);

  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleString();
};

const StaffPrivilegesSection = ({
  privileges,
  authority,
  loading,
  onAdd,
  onRevoke,
}) => {

  const active = privileges.filter(
    (item) => item.status === "active"
  );

  const history = privileges.filter(
    (item) => item.status !== "active"
  );

  const effective =
    authority?.effective_capabilities || [];

  return (
    <div className="space-y-5">

      <Card className="p-6 border-[#E2E8F0]">

        <div className="flex justify-between gap-4 flex-wrap">

          <div>
            <SectionTitle>
              Access & Privileges
            </SectionTitle>

            <p className="text-sm text-[#475569] mt-2 max-w-3xl">
              Give this staff member only the operational access required
              for their assignment without promoting them to a broader role.
            </p>
          </div>

          <Button
            onClick={onAdd}
            className="bg-[#1B2C5A] hover:bg-[#14224A]"
          >
            <Plus className="w-4 h-4 mr-1.5" />
            Add Access Rule
          </Button>

        </div>

        {loading ? (
          <p className="mt-5 text-sm text-[#475569]">
            Loading authority...
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-5">

            <DetailRow
              label="Effective Capabilities"
              value={effective.length}
            />

            <DetailRow
              label="Individual Rules"
              value={active.length}
            />

            <DetailRow
              label="Legacy Roles"
              value={
                (authority?.legacy_roles || [])
                  .map((role) => ROLE_LABELS[role] || role)
                  .join(", ") || "None"
              }
            />

          </div>
        )}

      </Card>

      <Card className="p-6 border-[#E2E8F0]">

        <SectionTitle>
          Individual Access Rules
        </SectionTitle>

        {active.length === 0 && !loading && (
          <div className="mt-4 border border-dashed rounded-lg p-6 text-center text-sm text-[#64748B]">
            No individual access rules. This staff member currently relies
            on inherited authority.
          </div>
        )}

        <div className="space-y-3 mt-4">

          {active.map((item) => (

            <div
              key={item.id}
              className="border rounded-lg p-4"
            >

              <div className="flex justify-between gap-4 flex-wrap">

                <div>

                  <div className="flex gap-2 items-center flex-wrap">

                    <strong>
                      {capabilityLabel(item.capability)}
                    </strong>

                    <Badge
                      variant={
                        item.effect === "restrict"
                          ? "danger"
                          : "success"
                      }
                    >
                      {item.effect === "restrict"
                        ? "Restricted"
                        : "Granted"}
                    </Badge>

                  </div>

                  <p className="text-xs font-mono text-[#64748B] mt-1">
                    {item.capability}
                  </p>

                </div>

                <Button
                  size="sm"
                  variant="outline"
                  className="text-red-700"
                  onClick={() => onRevoke(item)}
                >
                  Revoke
                </Button>

              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4 text-sm">

                <div>
                  <span className="text-[#64748B]">
                    Scope
                  </span>
                  <div>
                    {scopeLabel(item.scope)}
                  </div>
                </div>

                <div>
                  <span className="text-[#64748B]">
                    Starts
                  </span>
                  <div>
                    {privilegeDate(item.start_at)}
                  </div>
                </div>

                <div>
                  <span className="text-[#64748B]">
                    Ends
                  </span>
                  <div>
                    {privilegeDate(item.end_at)}
                  </div>
                </div>

              </div>

              <div className="mt-3 bg-[#F8FAFC] rounded p-3 text-sm">
                <span className="text-[#64748B]">
                  Reason:
                </span>{" "}
                {item.reason || "—"}
              </div>

            </div>

          ))}

        </div>

      </Card>

      <Card className="p-6 border-[#E2E8F0]">

        <SectionTitle>
          Effective Authority
        </SectionTitle>

        <p className="text-xs text-[#64748B] mt-1">
          Final access after roles, positions, assignments,
          delegations, individual grants and restrictions are combined.
        </p>

        <div className="flex flex-wrap gap-2 mt-4">

          {effective.map((capability) => (

            <span
              key={capability}
              title={capability}
              className="rounded-full border px-2.5 py-1 text-xs"
            >
              {capabilityLabel(capability)}
            </span>

          ))}

        </div>

      </Card>

      {history.length > 0 && (

        <Card className="p-6 border-[#E2E8F0]">

          <SectionTitle>
            Privilege History
          </SectionTitle>

          <div className="space-y-2 mt-4">

            {history.map((item) => (

              <div
                key={item.id}
                className="border rounded p-3 text-sm"
              >

                <strong>
                  {capabilityLabel(item.capability)}
                </strong>

                <span className="ml-2 text-[#64748B]">
                  {item.status}
                </span>

                <div className="text-xs mt-1">
                  {item.revocation_reason ||
                   item.reason ||
                   "—"}
                </div>

              </div>

            ))}

          </div>

        </Card>

      )}

    </div>
  );
};

const SectionTitle = ({ children }) => (
  <h2 className="font-display text-lg text-[#1B2C5A] flex items-center gap-2 before:content-[''] before:w-1 before:h-5 before:bg-[#9DCD3F] before:rounded-full">{children}</h2>
);

const Field = ({ label, required, children, full }) => (
  <div className={"space-y-1.5 " + (full ? "md:col-span-2 lg:col-span-3" : "")}>
    <Label className="text-[#1B2C5A] text-sm">{label}{required && <span className="text-red-600 ml-1">*</span>}</Label>
    {children}
  </div>
);

const Stat = ({ label, value }) => (
  <div>
    <div className="overline text-[#9DCD3F]">{label}</div>
    <div className="text-sm text-[#1B2C5A] font-medium truncate">{value}</div>
  </div>
);

const Badge = ({ children, variant }) => (
  <span className={"text-xs px-2.5 py-1 rounded-full border " + (
    variant === "success"
      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
      : variant === "danger"
      ? "bg-red-50 text-red-700 border-red-200"
      : "bg-[#1B2C5A]/5 text-[#1B2C5A] border-[#1B2C5A]/15"
  )}>{children}</span>
);

const LoginAccessSection = ({ login, loginEdit, setLoginEdit, loginForm, setLoginForm, saveLoginEdit, resetPassword, revokeLogin, openCreate, copyToClipboard }) => {
  if (!login || !login.has_login) {
    return (
      <Card className="p-8 border-[#E2E8F0] text-center" data-testid="login-empty">
        <div className="w-14 h-14 mx-auto rounded-full bg-[#9DCD3F]/15 grid place-items-center text-[#1B2C5A]">
          <KeyRound className="w-6 h-6" />
        </div>
        <h3 className="font-display text-xl text-[#1B2C5A] mt-4">No login account yet</h3>
        <p className="text-sm text-[#475569] mt-2 max-w-md mx-auto">
          This staff member has an HR profile but cannot sign into the portal. Create a secure login with one click —
          a temporary password will be generated for them.
        </p>
        <Button onClick={openCreate} className="mt-5 bg-[#1B2C5A] hover:bg-[#14224A] text-white" data-testid="create-login-btn">
          <KeyRound className="w-4 h-4 mr-1.5" /> Create Login Credentials
        </Button>
      </Card>
    );
  }

  const fmtDate = (s) => s ? new Date(s).toLocaleString() : "Never";
  const isDisabled = login.account_status === "disabled";

  return (
    <Card className="p-6 border-[#E2E8F0]" data-testid="login-section">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <SectionTitle>Login & Access</SectionTitle>
          <p className="text-sm text-[#475569] mt-1">Manage this staff member's portal account, role, and password.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {!loginEdit && (
            <Button variant="outline" size="sm" onClick={() => setLoginEdit(true)} data-testid="login-edit-btn">Edit Login</Button>
          )}
          <Button variant="outline" size="sm" onClick={resetPassword} data-testid="login-reset-btn">
            <RotateCw className="w-3.5 h-3.5 mr-1" /> Reset Password
          </Button>
          <Button variant="outline" size="sm" onClick={revokeLogin} className="text-red-700 hover:text-red-800 border-red-200" data-testid="login-revoke-btn">
            <UserX className="w-3.5 h-3.5 mr-1" /> Revoke
          </Button>
        </div>
      </div>

      {!loginEdit ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-5">
          <DetailRow label="Login Email" value={login.email} action={<Button size="sm" variant="ghost" onClick={() => copyToClipboard(login.email)}><CopyIcon className="w-3.5 h-3.5" /></Button>} testId="login-email" />
          <DetailRow
            label={`Roles (${(login.roles || [login.role]).filter(Boolean).length})`}
            value={
              <div className="flex flex-wrap gap-1.5 mt-1">
                {(login.roles && login.roles.length ? login.roles : [login.role]).filter(Boolean).map((r) => (
                  <Badge key={r}>{ROLE_LABELS[r] || r}</Badge>
                ))}
              </div>
            }
            testId="login-roles"
          />
          <DetailRow label="Account Status" value={<Badge variant={isDisabled ? "danger" : "success"}>{login.account_status || "active"}</Badge>} testId="login-status" />
          <DetailRow label="Must Change Password" value={<Badge variant={login.must_change_password ? "danger" : "success"}>{login.must_change_password ? "Yes — on next login" : "No"}</Badge>} testId="login-mcp" />
          <DetailRow label="Last Login" value={fmtDate(login.last_login_at)} testId="login-last" />
          <DetailRow label="Last Login IP" value={login.last_login_ip || "—"} />
          <DetailRow label="Total Logins" value={login.login_count || 0} />
          <DetailRow label="Password Changed" value={fmtDate(login.password_changed_at)} />
          <DetailRow label="Account Created" value={fmtDate(login.created_at)} />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
          <Field label="Login Email"><Input value={loginForm.email} onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })} data-testid="login-edit-email" /></Field>
          <Field label="Account Status">
            <Select value={loginForm.account_status} onValueChange={(v) => setLoginForm({ ...loginForm, account_status: v })}>
              <SelectTrigger data-testid="login-edit-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="disabled">Disabled</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <div className="md:col-span-2">
            <Field label="Roles (multi-select — staff can access all selected dashboards)">
              <MultiRoleSelect
                value={loginForm.roles && loginForm.roles.length ? loginForm.roles : [loginForm.role]}
                onChange={(roles) => setLoginForm({ ...loginForm, roles, role: primaryRole(roles) })}
                testId="login-edit-roles"
              />
            </Field>
          </div>
          <div className="md:col-span-2 flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setLoginEdit(false)}>Cancel</Button>
            <Button onClick={saveLoginEdit} className="bg-[#1B2C5A] hover:bg-[#14224A] text-white" data-testid="login-edit-save">Save Changes</Button>
          </div>
        </div>
      )}
    </Card>
  );
};

const DetailRow = ({ label, value, action, testId }) => (
  <div className="flex items-start justify-between gap-2 p-3 rounded-lg bg-[#FDFBF7] border border-[#E2E8F0]" data-testid={testId}>
    <div className="min-w-0 flex-1">
      <div className="overline text-[#9DCD3F]">{label}</div>
      <div className="text-sm text-[#1B2C5A] mt-1 break-words">{value || "—"}</div>
    </div>
    {action}
  </div>
);

export default StaffProfile;
